import React, { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { ThreeDots } from "react-loader-spinner";
import { getApi } from "../utils/api";
import {
  API_GET_PROJECTS_BY_EMPLOYEE_ID,
  API_GET_EMPLOYEE_FILE,
} from "../config/Endpoints";
import { designationOptions } from "../options/designationOptions";
import { displayFormatDate } from "../utils/helpers";

const fetchMemberImage = async (photoPath, accessToken) => {
  if (!photoPath?.includes("photoDir")) return "../assets/profile.png";

  try {
    const res = await axios.get(API_GET_EMPLOYEE_FILE, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      params: { filepath: photoPath },
      responseType: "blob",
    });

    if (res.status === 200 && res.data instanceof Blob) {
      return URL.createObjectURL(res.data);
    }
  } catch (err) {
    console.error("Error fetching image:", err);
  }

  return "../assets/profile.png";
};

const ProjectDashboard = () => {
  const accessToken = Cookies.get("accessToken");
  const userData = JSON.parse(localStorage.getItem("userData"));
  const userId = userData?.id;
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [designations, setDesignations] = useState([]);
  const [memberImages, setMemberImages] = useState({});
  const [projectCounts, setProjectCounts] = useState([
    { label: "All", value: 0, type: "all" },
    { label: "Active", value: 0, type: "active" },
    { label: "Completed", value: 0, type: "completed" },
    { label: "On Hold", value: 0, type: "onhold" },
  ]);

  const calculateProgress = (sowStartDate, sowEndDate) => {
    if (!sowStartDate)
      return { percent: 0, isOngoing: false, isUpcoming: false };
    if (!sowEndDate) return { percent: 50, isOngoing: true, isUpcoming: false };

    const [startMonth, startDay, startYear] = sowStartDate.split("-");
    const [endMonth, endDay, endYear] = sowEndDate.split("-");

    const startDate = new Date(startYear, startMonth - 1, startDay);
    const endDate = new Date(endYear, endMonth - 1, endDay);
    const today = new Date();

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return { percent: 0, isOngoing: false, isUpcoming: false };
    }

    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

    const daysPassed = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));

    if (daysPassed <= 0) {
      return { percent: 0, isOngoing: false, isUpcoming: true };
    }

    if (today >= endDate) {
      return { percent: 100, isOngoing: false, isUpcoming: false };
    }

    const percentValue = Math.round((daysPassed / totalDays) * 100);

    return { percent: percentValue, isOngoing: false, isUpcoming: false };
  };
  const currentProject = employeeData?.[currentProjectIndex] || {};

  const allTeamMembers = [
    ...(currentProject.members || []).map((m) => ({
      id: m.id,
      name: m.fullName,
      designation: m.designation,
      profileImg: m.profileImg,
      status: m.status,
    })),

    ...(currentProject.shadowMembers || []).map((s) => ({
      id: s.id,
      name: s.shadowEmployeeName,
      designation: s.designation,
      profileImg: s.profileImg,
      status: s.status,
    })),
  ];

  const getDesignationLabel = (value) => {
    const option = designationOptions.find((opt) => opt.value === value);
    return option ? option.label : value || "N/A";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getApi(
          `${API_GET_PROJECTS_BY_EMPLOYEE_ID}/${userId}`,
          accessToken
        );

        const projectsData = response?.data?.data || {};
        const allProjects = projectsData?.projects || [];
        // const projects = allProjects.filter((proj) => proj.status === 0);
        // shadow member projects
        const shadowProjects = allProjects.filter((proj) =>
          proj.shadowMembers?.some(
            (shadow) => shadow.shadowEmployeeId === userId
          )
        );

        const projects = allProjects.filter((proj) => {
          const isActive = proj.status === 0;

          // Check if user is an active team member (status 1)
          const isActiveMember = proj.members?.some(
            (member) => member.employeeId === userId && member.status === 1 // only active members
          );

          // Check if user is an active shadow member (status 1)
          const isActiveShadow = proj.shadowMembers?.some(
            (shadow) =>
              shadow.shadowEmployeeId === userId && shadow.status === 1 // only active shadows
          );

          // Check if user holds a leadership role
          const isManager =
            proj.projectManagerId === userId ||
            proj.principalSponsorId === userId ||
            proj.dlId === userId ||
            proj.leadBusinessAnalyst === userId;

          // FINAL condition:
          return isActive && (isActiveMember || isActiveShadow || isManager);
        });

        setEmployeeData(projects);

        const userDesignations = [];
        projects.forEach((project) => {
          if (project.projectManagerId === userId)
            userDesignations.push({
              designation: "Project Manager",
              projectName: project.projectName,
              projectId: project.projectId,
            });

          if (project.principalSponsorId === userId)
            userDesignations.push({
              designation: "Principal Sponsor",
              projectName: project.projectName,
              projectId: project.projectId,
            });

          if (project.dlId === userId)
            userDesignations.push({
              designation: "Delivery Lead",
              projectName: project.projectName,
              projectId: project.projectId,
            });

          if (project.leadBusinessAnalyst === userId)
            userDesignations.push({
              designation: "Lead Business Analyst",
              projectName: project.projectName,
              projectId: project.projectId,
            });

          const matchedMember = project.members?.find(
            (member) => member.employeeId === userId
          );
          if (matchedMember) {
            userDesignations.push({
              designation: getDesignationLabel(matchedMember.designation),
              projectName: project.projectName,
              projectId: project.projectId,
            });
          }
          const matchedShadow = project.shadowMembers?.find(
            (shadow) => shadow.shadowEmployeeId === userId
          );

          if (matchedShadow) {
            userDesignations.push({
              designation: matchedShadow.designation || "",
              projectName: project.projectName,
              projectId: project.projectId,
            });
          }
        });
        const uniqueDesignations = userDesignations.filter(
          (item, index, self) =>
            index === self.findIndex((d) => d.projectId === item.projectId)
        );

        setDesignations(uniqueDesignations);

        setProjectCounts([
          {
            label: "All",
            value: projectsData.totalProjects || projects.length || 0,
            type: "all",
          },
          {
            label: "Active",
            value: projectsData.activeProjects || 0,
            type: "active",
          },

          {
            label: "On Hold",
            value: projectsData.onHoldProjects || 0,
            type: "onhold",
          },
          {
            label: "Completed",
            value: projectsData.completedProjects || 0,
            type: "completed",
          },
        ]);
      } catch (error) {
        console.error("Error fetching project data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchAllImages = async () => {
      const allImages = {};

      const allMembers = [
        ...(currentProject.members || []),
        ...(currentProject.shadowMembers || []),
      ];

      for (const member of allMembers) {
        const img = await fetchMemberImage(member.profileImg, accessToken);
        allImages[member.id] = img;
      }

      setMemberImages(allImages);
    };

    if (currentProject) fetchAllImages();
  }, [currentProject]);

  if (!employeeData?.length || !designations?.length) {
    return null;
  }

  if (!currentProject) return <div></div>;

  const progress = calculateProgress(
    currentProject?.sowStartDate,
    currentProject?.sowEndDate
  );

  return (
    <div className="card  main-dashboard-card-radius ">
      <h3 className="heading">Projects</h3>

      <div className="project-section-wrapper">
        {/* Left Section */}
        <div className="project-left-section">
          {/* Project Count Boxes  */}
          <div className="project-count-boxes">
            {projectCounts.map((stat, idx) => (
              <div
                key={idx}
                className={`text-center p-2 shadow-sm d-flex flex-column justify-content-center project-count-box ${stat.type}`}
              >
                <div className="label-text">{stat.label}</div>
                <div className="value-text">{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Designations */}
          <div className="flex-fill border  bg-white shadow-sm px-2 py-3 d-flex flex-column designations-section main-dashboard-card-radius ">
            <h3 className="heading px-2">Active Projects</h3>
            <div className="overflow-auto active-projects-list">
              {designations.map((item, idx) => {
                const projectIndex = employeeData.findIndex(
                  (proj) => proj.projectId === item.projectId
                );
                const isActive = currentProjectIndex === projectIndex;

                return (
                  <div
                    key={idx}
                    className={`designation-card d-flex justify-content-between align-items-center border mb-2 cursor-link ${
                      isActive ? "active-project-card" : ""
                    }`}
                    onClick={() => {
                      if (projectIndex !== -1)
                        setCurrentProjectIndex(projectIndex);
                    }}
                  >
                    <div>
                      <div className="designation-title">
                        {item?.projectName || "Unnamed Project"}
                      </div>
                      <small>
                        <span className="fw-medium designation-project">
                          {item?.designation}
                        </span>
                      </small>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Right Section */}

        <div className="project-right-section bg-white shadow-sm px-4 py-3 main-dashboard-card-radius ">
          <div className="d-flex flex-column  flex-grow-1 active-projects">
            {/* Inner Card */}
            <div className="d-flex ps-3 pe-3 p-3 justify-content-start align-items-start position-relative project-card">
              <div className="flex-grow-1">
                <div className="d-flex gap-3 border-bottom align-items-center pb-2 px-2">
                  <h5 className="heading" style={{ color: "#5c63c0" }}>
                    {currentProject?.projectName || "N/A"}
                  </h5>
                  <p className="badge bg-white mb-1 ms-auto project-card-startDate-badge">
                    {displayFormatDate(
                      currentProject?.sowStartDate || "",
                      true
                    )}
                  </p>
                </div>

                <div class="project-detail px-2 mt-2">
                  <div class="detail-row">
                    <span class="detail-row-label">Principal Sponsor:</span>
                    <span class="value">
                      {" "}
                      {currentProject?.principalSponsorName || "N/A"}
                    </span>
                  </div>

                  <div class="detail-row">
                    <span class="detail-row-label">Delivery Lead:</span>
                    <span class="value">{currentProject?.dlName || "N/A"}</span>
                  </div>

                  {currentProject?.leadBusinessAnalystName &&
                    currentProject?.leadBusinessAnalystName !== "N/A" &&
                    currentProject?.leadBusinessAnalystName !== null && (
                      <div class="detail-row">
                        <span class="detail-row-label">
                          Lead Business Analyst:
                        </span>
                        <span class="value">
                          {currentProject?.leadBusinessAnalystName || "N/A"}
                        </span>
                      </div>
                    )}

                  <div class="detail-row">
                    <span class="detail-row-label">Project Manager:</span>
                    <span class="value">
                      {currentProject?.projectManagerName || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Responsive Progress Section */}
            <div className=" project-progress-card border bg-whitept-3 pb-3 mx-auto">
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap">
                <span className="fw-semibold project-progress-title">
                  Project Progress
                </span>
                <span className="fw-bold project-progress-percent">
                  {progress.isUpcoming
                    ? "Upcoming"
                    : progress.isOngoing
                    ? "Ongoing"
                    : `${progress.percent}%`}
                </span>
              </div>

              <div className="progress progress-custom">
                <div
                  className="progress-bar progress-bar-custom"
                  style={{
                    width: `${progress.percent}%`,
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="px-2 flex-grow-1 team-members-section bg-white shadow-sm border py-3 main-dashboard-card-radius  project--team">
            <h3 className="heading ps-3">Team Members</h3>
            <ul className="team-members-list overflow-auto">
              {allTeamMembers.filter((m) => m.status !== 0 && m.status !== 2)
                .length > 0 ? (
                allTeamMembers
                  .filter((m) => m.status !== 0 && m.status !== 2)
                  .map((member) => (
                    <li key={member.id} className="team-member-item border">
                      <div className="d-flex flex-direction-column gap-2 w-100">
                        <img
                          src={
                            memberImages[member.id] || "../assets/profile.png"
                          }
                          alt={member.firstName}
                          className="member-avatar"
                          width="90px"
                          height="90px"
                        />
                        <div className="member-info">
                          <span className="member-name">{member.name}</span>

                          <span className="member-designation">
                            {getDesignationLabel(member.designation)}
                          </span>
                        </div>
                      </div>
                    </li>
                  ))
              ) : (
                <p className="fs-6 text-secondary text-center">
                  No records Available
                </p>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
